/**
 * Validadores de Documentos Brasileiros
 * CPF, CNH e Renavam com validação algorítmica REAL
 */

/**
 * Valida CPF usando algoritmo Módulo 11
 * @param cpf - CPF com ou sem formatação
 * @returns true se válido, false se inválido
 */
export function validateCPF(cpf: string): boolean {
    // Remove caracteres não numéricos
    const cleaned = cpf.replace(/\D/g, '');

    // Deve ter 11 dígitos
    if (cleaned.length !== 11) return false;

    // Rejeita CPFs com todos os dígitos iguais (ex: 111.111.111-11)
    if (/^(\d)\1{10}$/.test(cleaned)) return false;

    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cleaned.charAt(i)) * (10 - i);
    }
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleaned.charAt(9))) return false;

    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cleaned.charAt(i)) * (11 - i);
    }
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleaned.charAt(10))) return false;

    return true;
}

/**
 * Valida CNH (Carteira Nacional de Habilitação)
 * @param cnh - CNH com 11 dígitos
 * @returns true se válido, false se inválido
 */
export function validateCNH(cnh: string): boolean {
    const cleaned = cnh.replace(/\D/g, '');

    // Deve ter 11 dígitos
    if (cleaned.length !== 11) return false;

    // Rejeita CNHs com todos os dígitos iguais
    if (/^(\d)\1{10}$/.test(cleaned)) return false;

    // Algoritmo de validação da CNH
    let dsc = 0;
    let sum = 0;

    // Primeiro dígito verificador
    for (let i = 0, j = 9; i < 9; i++, j--) {
        sum += parseInt(cleaned.charAt(i)) * j;
    }

    let digit1 = sum % 11;
    if (digit1 >= 10) {
        digit1 = 0;
        dsc = 2;
    }

    // Segundo dígito verificador
    sum = 0;
    for (let i = 0, j = 1; i < 9; i++, j++) {
        sum += parseInt(cleaned.charAt(i)) * j;
    }

    let rest = sum % 11;
    let digit2 = rest >= 10 ? 0 : rest - dsc;
    if (digit2 < 0) digit2 = digit2 + 11;

    // Compara os dígitos calculados com os informados
    const expectedDigits = `${digit1}${digit2}`;
    const providedDigits = cleaned.substring(9);

    return expectedDigits === providedDigits;
}

/**
 * Valida Renavam (Registro Nacional de Veículos Automotores)
 * Algoritmo oficial do DENATRAN
 * @param renavam - Renavam com 11 dígitos
 * @returns true se válido, false se inválido
 */
export function validateRenavam(renavam: string): boolean {
    const cleaned = renavam.replace(/\D/g, '');

    // Deve ter 11 dígitos
    if (cleaned.length !== 11) return false;

    // Rejeita Renavams com todos os dígitos iguais
    if (/^(\d)\1{10}$/.test(cleaned)) return false;

    // Algoritmo de validação do Renavam (módulo 11)
    const weights = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;

    // Soma ponderada dos 10 primeiros dígitos
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cleaned.charAt(i)) * weights[i];
    }

    // Calcula o dígito verificador
    let remainder = sum % 11;
    let verifierDigit = 11 - remainder;

    // Se o resultado for 10 ou 11, o dígito verificador é 0
    if (verifierDigit >= 10) {
        verifierDigit = 0;
    }

    // Compara com o último dígito informado
    return verifierDigit === parseInt(cleaned.charAt(10));
}

/**
 * Aplica máscara de CPF
 * @param value - Valor a ser formatado
 * @returns CPF formatado (999.999.999-99)
 */
export function maskCPF(value: string): string {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    return cleaned
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/**
 * Aplica máscara de telefone
 * @param value - Valor a ser formatado
 * @returns Telefone formatado ((99) 99999-9999)
 */
export function maskPhone(value: string): string {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    return cleaned
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
}

/**
 * Aplica máscara de CEP
 * @param value - Valor a ser formatado
 * @returns CEP formatado (99999-999)
 */
export function maskCEP(value: string): string {
    const cleaned = value.replace(/\D/g, '').slice(0, 8);
    return cleaned.replace(/(\d{5})(\d)/, '$1-$2');
}

/**
 * Aplica máscara de placa de veículo (Mercosul ou antiga)
 * @param value - Valor a ser formatado
 * @returns Placa formatada (ABC-1D23 ou ABC-1234)
 */
export function maskVehiclePlate(value: string): string {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    if (cleaned.length <= 3) return cleaned;
    return cleaned.slice(0, 3) + '-' + cleaned.slice(3);
}

/**
 * Remove máscara de qualquer campo
 * @param value - Valor com máscara
 * @returns Valor apenas com números
 */
export function unmask(value: string): string {
    return value.replace(/\D/g, '');
}
